import React, { useState } from 'react';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    ListItemButton,
    MenuItem,
    Radio,
    RadioGroup,
    styled
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import { Collapsible } from '../../../../components/common/collapsible';
import { Error } from '../../../../components/forms/styles';
import { ClassContainer } from '../../ClassContainer';
import TemplateOptions from '../../TemplateOptions';
import { CanJoinBeforeInstructor, RequireAuth, WaitingRoom } from '../../types';
import { StyledFieldset, StyledFieldsetAdvanced } from '../index.styles';
import Instructors from './Instructors';
import ControlledSelect from '../../../../components/forms/ControlledSelect';
import { ContentLearningTools } from '../../../../types/apiTypes';
import { WebExpOptions } from '../../../../types/adminConfig';
import UnauthorizedUserAccessClassSetting from './UnauthorizedUserAccessClassSetting';
import { showSettings } from '../../utils';

const ExpandMoreButton = styled(ListItemButton)`
    cursor: pointer;
    display: flex;
    justify-content: flex-start;
    font-size: 1rem;

    padding: 1rem 0.5rem;
    margin: 0 0.75rem;

    border: 0;
    outline: 0;

    .MuiButtonBase-root:hover {
        background-color: transparent;
    }
`;

const SectionTitle = styled('div')`
    font-weight: 600;
    margin-left: 0.5rem;
`;

const StyledFormControl = styled(FormControl)`
    padding-right: 24px;
`;

const StyledFullWidthContainer = styled('div')`
    grid-column: span 2;
`;

const Advanced = ({ parentPage }) => {
    const { t } = useTranslation('calendar');
    const { t: tAdminSettings } = useTranslation('admin_settings');

    const {
        control,
        publishedTemplates,
        formActionType,
        allowForcedAuthentication,
        zoomAppId,
        ltiState,
        lmsSyncSettings,
        schoolOptions
    } = useContainer(ClassContainer);

    const showUnauthedUserAccessOptions = showSettings({
        options: schoolOptions,
        overrideName: 'authenticationOverride',
        parentPage
    });
    const showAllowVanillaZoom = showSettings({
        options: schoolOptions,
        overrideName: 'allowVanillaZoomOverride',
        parentPage
    });
    const showWebExperienceToggle = showSettings({
        options: schoolOptions,
        overrideName: 'webExperienceOverride',
        parentPage
    });

    const [expanded, setExpanded] = useState(false);

    const handleClick = () => {
        setExpanded((previous) => !previous);
    };

    const showTemplateOption = publishedTemplates.length > 0 && formActionType === 'CREATE';
    const showContentTypeOption =
        lmsSyncSettings.sync_enabled &&
        lmsSyncSettings.enable_content &&
        lmsSyncSettings.content_learning_tools === ContentLearningTools.InstructorChoice &&
        Object.keys(ltiState).length !== 0;

    const isTeams = zoomAppId === 'teams';

    return (
        <>
            <ExpandMoreButton aria-controls="advanced-section" onClick={handleClick}>
                {expanded ? <ExpandLess /> : <ExpandMore />}
                <SectionTitle>{t('advanced-settings')}</SectionTitle>
            </ExpandMoreButton>
            <Collapsible id="advanced-section" expanded={expanded}>
                {showTemplateOption && (
                    <StyledFieldset>
                        <Controller
                            name="courseTemplate"
                            control={control}
                            render={(props) => (
                                <>
                                    <FormControlLabel
                                        label={t('use-a-course-template')}
                                        control={<Checkbox {...props.field} />}
                                    />
                                    {props?.fieldState?.error ? <Error>{props.fieldState.error.message}</Error> : null}
                                </>
                            )}
                        />
                        <TemplateOptions />
                    </StyledFieldset>
                )}
                <StyledFieldsetAdvanced>
                    {showContentTypeOption && (
                        <StyledFormControl>
                            <ControlledSelect
                                defaultValue="lms"
                                name="contentType"
                                label={t('content-type')}
                                control={control}
                            >
                                <MenuItem value={'native'}>{t('content-type-class')}</MenuItem>
                                <MenuItem value={'lms'}>{t('content-type-lms')}</MenuItem>
                            </ControlledSelect>
                        </StyledFormControl>
                    )}
                    {!isTeams && zoomAppId !== undefined && (
                        <Controller
                            control={control}
                            name="waitingRoom"
                            render={({ field }) => (
                                <div>
                                    <FormLabel id="waiting-room-toggle">{t('waiting-room')}</FormLabel>
                                    <RadioGroup aria-labelledby="waiting-room-toggle" {...field} row>
                                        <FormControlLabel value={WaitingRoom.ON} control={<Radio />} label={t('on')} />
                                        <FormControlLabel
                                            value={WaitingRoom.OFF}
                                            control={<Radio />}
                                            label={t('off')}
                                        />
                                    </RadioGroup>
                                </div>
                            )}
                        />
                    )}
                    <Controller
                        control={control}
                        name="canJoinBeforeInstructor"
                        render={({ field }) => (
                            <div>
                                <FormLabel id="can-join-before-instructor">
                                    {t('students-can-join-before-instructor')}
                                </FormLabel>
                                <RadioGroup aria-labelledby="can-join-before-instructor" {...field} row>
                                    <FormControlLabel
                                        value={CanJoinBeforeInstructor.YES}
                                        control={<Radio />}
                                        label={t('yes')}
                                    />
                                    <FormControlLabel
                                        value={CanJoinBeforeInstructor.NO}
                                        control={<Radio />}
                                        label={t('no')}
                                    />
                                </RadioGroup>
                            </div>
                        )}
                    />
                    {allowForcedAuthentication && (
                        <Controller
                            control={control}
                            name="requireAuth"
                            render={({ field }) => (
                                <div>
                                    <FormLabel id="require-auth">{t('require-authentication')}</FormLabel>
                                    <RadioGroup aria-labelledby="require-auth" {...field} row>
                                        <FormControlLabel value={RequireAuth.ON} control={<Radio />} label={t('on')} />
                                        <FormControlLabel
                                            value={RequireAuth.OFF}
                                            control={<Radio />}
                                            label={t('off')}
                                        />
                                    </RadioGroup>
                                </div>
                            )}
                        />
                    )}
                    {showWebExperienceToggle && (
                        <Controller
                            control={control}
                            name="webExperience"
                            render={({ field }) => (
                                <div>
                                    <FormLabel id="web-experience-label">{t('web-experience')}</FormLabel>

                                    <RadioGroup aria-labelledby="web-experience-label" {...field} row>
                                        <FormControlLabel
                                            value={WebExpOptions.Classic}
                                            control={<Radio />}
                                            label={t('classic')}
                                        />
                                        <FormControlLabel
                                            value={WebExpOptions.NextGen}
                                            control={<Radio />}
                                            label={t('nextgen')}
                                        />
                                    </RadioGroup>
                                </div>
                            )}
                        />
                    )}
                    {showAllowVanillaZoom && (
                        <Controller
                            control={control}
                            name="allowVanillaZoom"
                            render={({ field }) => (
                                <StyledFullWidthContainer>
                                    <FormControlLabel
                                        label={tAdminSettings('configuration.allow-vanilla-zoom')}
                                        control={<Checkbox checked={field.value} {...field} />}
                                    />
                                </StyledFullWidthContainer>
                            )}
                        />
                    )}
                    {showUnauthedUserAccessOptions && (
                        <StyledFullWidthContainer>
                            <UnauthorizedUserAccessClassSetting />
                        </StyledFullWidthContainer>
                    )}
                </StyledFieldsetAdvanced>
                <Instructors />
            </Collapsible>
        </>
    );
};

export default Advanced;
